import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { showError } from '@utils';
import { boardsServices, fleetVehiclesServices } from '@services';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees } from 'hooks/common';
import {
  filterTrafficOffensesAssignees,
  handleTrafficOffensesTaskData,
  handleVehicleData,
} from './utils';
import { TRAFFIC_OFFENSES_BOARD } from './constants';

const stages = TRAFFIC_OFFENSES_BOARD?.statusOrder?.reduce((acc, cur) => {
  acc.push({
    text: TRAFFIC_OFFENSES_BOARD.statuses[cur].label,
    value: Number(cur),
  });
  return acc;
}, []);

export const useTrafficOffensesTaskOperations = () => {
  const {
    assignees: cachedAssignees,
    isFetching: isAssigneesFetching,
    isFetched: isAssigneesFetched,
  } = useAssignees();
  const router = useRouter();
  const trafficOffensesTaskId = router.query?.trafficOffensesTaskId;

  const assignees = filterTrafficOffensesAssignees(cachedAssignees);

  const taskResponse = useQuery(
    [QUERY_KEYS.GET_TRAFFIC_OFFENSES_TASK_KEY, trafficOffensesTaskId],
    () => boardsServices.getTaskById(trafficOffensesTaskId),
    {
      enabled:
        isAssigneesFetched && assignees.length > 0 && !!trafficOffensesTaskId,
    }
  );

  const task = handleTrafficOffensesTaskData(
    taskResponse?.data?.data,
    assignees,
    stages
  );
  const carId = taskResponse?.data?.data?.customFields?.carId;

  const vehicleResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_BY_INTERNAL_ID_KEY, trafficOffensesTaskId],
    () => fleetVehiclesServices.getVehicleByInternalId(carId),
    {
      enabled: taskResponse.isFetched && !!carId,
    }
  );

  const vehicle = handleVehicleData(vehicleResponse?.data?.data);

  const loading =
    isAssigneesFetching ||
    taskResponse.isFetching ||
    vehicleResponse.isFetching;

  const updateTask = async (payload = {}) => {
    try {
      if (payload?.cardId) {
        const taskId = payload?.cardId;
        delete payload?.cardId;
        await boardsServices.updateTask(taskId, payload);
      } else {
        await boardsServices.updateTask(payload?.id, payload);
      }
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (taskResponse?.isError) {
      showError(taskResponse?.error);
    }
  }, [taskResponse?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (vehicleResponse?.isError) {
      showError(vehicleResponse?.error);
    }
  }, [vehicleResponse?.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading,
    task,
    taskId: task?.id,
    stages,
    agents: assignees,
    vehicle,
    assignees,
    updateTask,
    getTrafficOffensesTask: taskResponse?.refetch,
  };
};

export default useTrafficOffensesTaskOperations;
